import { selectQuotationClosedBoxAccessories } from "app/containers/WorkOrder/redux/selector";
import { actions } from "app/containers/WorkOrder/redux/slice";
import {
  ClosedBoxAccessoryCard,
  QuotationClosedBoxAccessoryCardKey,
} from "app/containers/WorkOrder/types";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { NOTE } from "../../../hooks/useQuoteBoxStructure";
import { QuoteSelectOption } from "../../../types";
import {
  FieldsRow,
  StructureCard,
} from "../OpenBoxQuotes/BoxStructureFields";
import { NoteField, NumberField, SelectField } from "../OpenBoxQuotes/FormFields";
import { closedKey } from "./BoxStructureCards";

export const useClosedBoxAccessories = () => {
  const dispatch = useDispatch();
  const data = useSelector(selectQuotationClosedBoxAccessories);
  const patch = (
    key: QuotationClosedBoxAccessoryCardKey,
    value: Partial<ClosedBoxAccessoryCard>
  ) => dispatch(actions.updateQuotationClosedBoxAccessories({ key, value }));

  return { data, patch };
};

type CardProps = {
  cardKey: QuotationClosedBoxAccessoryCardKey;
  titleKey: string;
};

const resolvedNote = (note: string, t: (key: string) => string) =>
  note.startsWith("QUOTES.") ? t(note) : note;

export const AccessoryNoteCard = ({ cardKey, titleKey }: CardProps) => {
  const { data, patch } = useClosedBoxAccessories();
  const item = data[cardKey];

  return (
    <StructureCard
      enabled={item.enabled}
      titleKey={titleKey}
      onToggle={(enabled) => patch(cardKey, { enabled })}
    >
      <NoteField
        labelKey={NOTE}
        placeholderKey={NOTE}
        value={item.note}
        onChange={(note) => patch(cardKey, { note })}
      />
    </StructureCard>
  );
};

export const AccessoryQuantityCard = ({ cardKey, titleKey }: CardProps) => {
  const { data, patch } = useClosedBoxAccessories();
  const item = data[cardKey];

  return (
    <StructureCard
      enabled={item.enabled}
      titleKey={titleKey}
      onToggle={(enabled) => patch(cardKey, { enabled })}
    >
      <NumberField
        labelKey={closedKey("FIELDS.QUANTITY")}
        value={item.quantity}
        onChange={(quantity) => patch(cardKey, { quantity })}
      />
    </StructureCard>
  );
};

export const AccessoryTypeCard = ({
  cardKey,
  titleKey,
  options,
}: CardProps & { options: QuoteSelectOption[] }) => {
  const { data, patch } = useClosedBoxAccessories();
  const item = data[cardKey];

  return (
    <StructureCard
      enabled={item.enabled}
      titleKey={titleKey}
      onToggle={(enabled) => patch(cardKey, { enabled })}
    >
      <SelectField
        labelKey={closedKey("FIELDS.TYPE")}
        value={item.selectedOption}
        options={options}
        onChange={(selectedOption) => patch(cardKey, { selectedOption })}
      />
    </StructureCard>
  );
};

export const AccessoryQuantityNoteCard = ({ cardKey, titleKey }: CardProps) => {
  const { t } = useTranslation();
  const { data, patch } = useClosedBoxAccessories();
  const item = data[cardKey];
  const note = resolvedNote(item.note, t);

  return (
    <StructureCard
      enabled={item.enabled}
      titleKey={titleKey}
      onToggle={(enabled) => patch(cardKey, { enabled })}
    >
      <FieldsRow>
        <NumberField
          labelKey={closedKey("FIELDS.QUANTITY")}
          value={item.quantity}
          onChange={(quantity) => patch(cardKey, { quantity })}
          compact
        />
        <NoteField
          labelKey={closedKey("FIELDS.NOTE_LOCATION_SIZE")}
          placeholderKey={closedKey("FIELDS.NOTE_LOCATION_SIZE")}
          value={note}
          onChange={(nextNote) => patch(cardKey, { note: nextNote })}
          grow
        />
      </FieldsRow>
    </StructureCard>
  );
};

export const AccessoryTypeQuantityNoteCard = ({
  cardKey,
  titleKey,
  options,
}: CardProps & { options: QuoteSelectOption[] }) => {
  const { t } = useTranslation();
  const { data, patch } = useClosedBoxAccessories();
  const item = data[cardKey];
  const note = resolvedNote(item.note, t);

  return (
    <StructureCard
      enabled={item.enabled}
      titleKey={titleKey}
      onToggle={(enabled) => patch(cardKey, { enabled })}
    >
      <FieldsRow>
        <SelectField
          labelKey={closedKey("FIELDS.TYPE")}
          value={item.selectedOption}
          options={options}
          onChange={(selectedOption) => patch(cardKey, { selectedOption })}
          grow
        />
        <NumberField
          labelKey={closedKey("FIELDS.QUANTITY")}
          value={item.quantity}
          onChange={(quantity) => patch(cardKey, { quantity })}
          compact
        />
        <NoteField
          labelKey={closedKey("FIELDS.NOTE_LOCATION_SIZE")}
          placeholderKey={closedKey("FIELDS.NOTE_LOCATION_SIZE")}
          value={note}
          onChange={(nextNote) => patch(cardKey, { note: nextNote })}
          grow
        />
      </FieldsRow>
    </StructureCard>
  );
};
